// Agent api fns — carved verbatim from `src/api/scribeAPIV2.ts` (which now
// re-exports or wraps every one of them) as D1 of the `calls/agents` family
// (ARCFE-1097). Transport comes from the `@heidi/api` configured-client
// registry (E5): `http()` returns the app's real `scribeHttpV2` singleton, so
// request behaviour is byte-identical.
//
// Twenty-three of the twenty-five `@domain calls` endpoints the hub still held
// land here. The other two are DELETED, not carved — see the hub's own carve
// banner and the family record.
//
// FROZEN ASYMMETRIES, transcribed rather than tidied. Each of these looks like
// an inconsistency and is a request behaviour:
//   - `getCallAgents` filters `skip`/`limit` on `!== undefined` but the other
//     four params on truthiness, so `search: ''`, `sort_by: ''` and
//     `skip_auto_seed: false` are OMITTED from the query string.
//   - `purchaseCallAgentPhoneNumber`, `recompileAgent`'s siblings and the two
//     scenario POSTs send an explicit `{}` body; dropping it changes the
//     Content-Length axios sends.
//   - `getCallAgentWebRTCToken` falls back to the agent ID as the agent NAME.
//   - `searchClinics` always sends `region`, even as `undefined`.
//   - `getLastAgentSimulationRun` passes the persona through `params`, not the
//     path.
// None of them is encoded with `encodeURIComponent`, unlike the reminders and
// ehr-links slices. That is how the hub wrote them and it is preserved.
//
// THE GUARDRAILS-V2 FLAG CROSSES AS A GETTER, NOT A VALUE (ruled at
// ARCFE-1060, applied here). Three fns branch on
// `IS_COMMS_GUARDRAILS_V2_ENABLED`, an app flag a package may not read. It
// arrives as `isGuardrailsV2Enabled: () => boolean` and is called at REQUEST
// time, exactly where the hub called it. The laziness is load-bearing rather
// than stylistic: the flag resolves through `posthog.isFeatureEnabled`, which
// EMITS a `$feature_flag_called` exposure event on read, so resolving it
// eagerly at a call site would fire exposure events for requests that never
// reach the branch. The three hub entries therefore WRAP instead of
// re-exporting, which the ruling records as a normal delegate: the alternatives
// were reading the flag in the package (forbidden) or forking one route into
// two named endpoints (barred by the frozen-identity rule).
import type { AgentPaginationParams } from '../../contracts/calls';
import {
  GUARDRAILS_API_VERSION_HEADER,
  type GuardrailCheckResponseV2,
} from '../../contracts/guardrails';
import type {
  Agent,
  AgentBuilderChatResponse,
  AgentResponse,
  AgentScenario,
  AgentSimulationRun,
  CallContextValues,
  QARunResult,
  UpdateAgentRequest,
} from '../../contracts/types';
import type {
  VoicePreviewRequestBody,
  VoicesCatalogView,
} from '../../contracts/voice-catalog';
import { http } from './http';

/**
 * How the guardrails-v2 feature flag reaches this layer.
 *
 * A getter, never a resolved boolean — see the file header for the exposure-
 * event reason. The package never learns the flag's KEY, only that someone
 * app-side can answer the question.
 */
export type IsGuardrailsV2Enabled = () => boolean;

/**
 * Build the per-request config that opts a call into the v2 guardrail service.
 *
 * Exported because the hub's `getGuardrailsV2RequestConfig` — a name its
 * compat shim re-exports and its own test names — now delegates here rather
 * than keeping a second copy of the header logic.
 */
export const guardrailsV2RequestConfig = (
  isGuardrailsV2Enabled: IsGuardrailsV2Enabled
) => {
  if (!isGuardrailsV2Enabled()) return undefined;
  return {
    headers: {
      [GUARDRAILS_API_VERSION_HEADER]: '2',
    },
  };
};

/** @domain calls */
export const getCallAgents = (params: AgentPaginationParams) => {
  const queryParams: Record<string, unknown> = {};
  if (params.search) queryParams.search = params.search;
  if (params.skip !== undefined) queryParams.skip = params.skip;
  if (params.limit !== undefined) queryParams.limit = params.limit;
  if (params.sort_by) queryParams.sort_by = params.sort_by;
  if (params.skip_auto_seed) queryParams.skip_auto_seed = params.skip_auto_seed;

  return http().get<AgentResponse>('/calls/agents', {
    params: queryParams,
  });
};

/** @domain calls */
export const getCallAgent = (agentId: string) => {
  return http().get<Agent>(`/calls/agents/${agentId}`);
};

/** @domain calls */
export const purchaseCallAgentPhoneNumber = (agentId: string) => {
  return http().post<Agent>(`/calls/agents/${agentId}/phone-number`, {});
};

/** @domain calls */
export const getCallAgentWebRTCToken = (agentId: string, agentName?: string) => {
  return http().post<{ token: string; identity: string; agent_name: string }>(
    `/calls/agents/${agentId}/webrtc-token`,
    { agent_name: agentName ?? agentId }
  );
};

/** @domain calls */
export const getAgentPredictedParameters = (agentId: string, taskId: string) => {
  return http().get<CallContextValues>(`/calls/agents/${agentId}/predicted-parameters`, {
    params: { task_id: taskId },
  });
};

/** @domain calls */
export const commitAgentFromObjective = (
  params: {
    goal: string;
    direction: string;
    context?: string;
    knowledge_base?: string;
    practice_name?: string;
    practice_address?: string;
    practice_country?: string;
    handoff_phone?: string;
    source?: string;
    /**
     * Optional config fields applied as a follow-up update_agent call server-side
     * after the receptionist commit returns. Lets self-serve onboarding ship the
     * full agent config in one round-trip. Older ml-scribe deploys ignore this
     * field (Pydantic drops unknown keys).
     */
    config_overrides?: Record<string, unknown>;
  },
  isGuardrailsV2Enabled: IsGuardrailsV2Enabled,
  options?: {
    /**
     * When `true`, sends `X-Skip-Guardrails: true` so ml-scribe / the
     * receptionist bypass content-safety guardrails on this commit AND on the
     * chained `update_agent` it triggers. Callers must have already
     * pre-checked all user-typed fields (`objective`, `appointment_rules`)
     * upstream — opt-in per request.
     */
    skipGuardrails?: boolean;
  }
) => {
  const baseConfig = guardrailsV2RequestConfig(isGuardrailsV2Enabled);
  const headers: Record<string, string> = { ...(baseConfig?.headers ?? {}) };
  if (options?.skipGuardrails) {
    headers['X-Skip-Guardrails'] = 'true';
  }
  const requestConfig = Object.keys(headers).length > 0 ? { headers } : undefined;
  return http().post<Agent>(`/calls/agents/commit`, params, requestConfig);
};

/** @domain calls */
export const recompileAgent = (
  agentId: string,
  params: {
    goal: string;
    context?: string;
    direction?: string;
    source?: string;
  }
) => {
  return http().post<Agent>(`/calls/agents/${agentId}/recompile`, params);
};

/** @domain calls */
export const checkGuardrail = (
  field: string,
  value: string,
  isGuardrailsV2Enabled: IsGuardrailsV2Enabled
) => {
  if (isGuardrailsV2Enabled()) {
    return http().post<GuardrailCheckResponseV2>('/calls/agents/v2/guardrails/check', {
      field,
      value,
    });
  }

  return http().post<{ passed: boolean; category: string | null }>(
    '/calls/agents/guardrails/check',
    { field, value }
  );
};

/** @domain calls */
export const updateCallAgent = (
  agentId: string,
  agent: UpdateAgentRequest,
  isGuardrailsV2Enabled: IsGuardrailsV2Enabled
) => {
  return http().patch(
    `/calls/agents/${agentId}`,
    agent,
    guardrailsV2RequestConfig(isGuardrailsV2Enabled)
  );
};

/** @domain calls */
export const deleteCallAgent = (agentId: string) => {
  return http().delete(`/calls/agents/${agentId}`);
};

// Voice & language catalog (ml-scribe BFF, proxies ai-receptionist-be /voices).
/** @domain calls */
export const getCallVoiceCatalog = () => {
  return http().get<VoicesCatalogView>('/calls/voices');
};

/** @domain calls */
/** Returns the raw `audio/wav` bytes for a short voice preview. */
export const previewCallVoice = (body: VoicePreviewRequestBody) => {
  return http().post<ArrayBuffer>('/calls/voices/preview', body, {
    responseType: 'arraybuffer',
  });
};

// Agent Builder / Configuration Chat
/** @domain calls */
export const createAgentBuilderSession = (params: {
  agent_config_id: string;
  update_field?: string;
}) => {
  return http().post<{ session_id: string }>('/calls/agent-builder/sessions', params);
};

/** @domain calls */
export const sendAgentBuilderChat = (sessionId: string, message: string) => {
  return http().post<AgentBuilderChatResponse>(
    `/calls/agent-builder/sessions/${sessionId}/chat`,
    { message }
  );
};

// QA Testing -- 3-phase flow: refresh scenarios -> run QA -> get full results
//
// CARVED, NOT DELETED, AND THE MEASUREMENT SAYS DELETE. All three routes are
// reachable only from `useMutateAutoEval`, whose three hooks have zero
// production importers of their own (measured at HEAD, ARCFE-1097) — so the
// whole three-phase flow is dead below the UI. They are carried anyway, with
// the finding recorded as a blocker on the family record rather than acted on
// here: the P0.2 zero-consumer rule permits "delete OR record a keep-reason",
// and retiring a pod's staged-but-unwired product flow is that pod's call, not
// a migration PR's. The two endpoints this carve DOES delete
// (`generateAgentPreview`, `getLatestAgentBuilderSession`) were already ruled
// dead by the child change's own D1 line before this pass started.
/** @domain calls */
export const refreshAgentScenarios = (agentConfigId: string) =>
  http().post<{
    scenarios: Array<{ id: string; description: string }>;
    total: number;
  }>(`/calls/agent-builder/agents/${agentConfigId}/scenarios/refresh`, {});

/** @domain calls */
export const runAgentQA = (agentConfigId: string) =>
  http().post<{ run_id: string; overall_score: number; passed: boolean }>(
    `/calls/agent-builder/agents/${agentConfigId}/qa/run`,
    {}
  );

/** @domain calls */
export const getAgentQARun = (agentConfigId: string, runId: string) =>
  http().get<QARunResult>(
    `/calls/agent-builder/agents/${agentConfigId}/qa/runs/${runId}`
  );

/** @domain calls */
export const listAgentScenarios = (agentConfigId: string) =>
  http().get<{ scenarios: AgentScenario[] }>(`/calls/agents/${agentConfigId}/scenarios`);

/** @domain calls */
export const regenerateAgentScenarios = (agentConfigId: string) =>
  http().post<{ scenarios: AgentScenario[] }>(
    `/calls/agents/${agentConfigId}/scenarios/regenerate`,
    {}
  );

/** @domain calls */
export const addCustomAgentScenario = (agentConfigId: string, persona: AgentScenario) =>
  http().post<{ scenarios: AgentScenario[] }>(
    `/calls/agents/${agentConfigId}/scenarios/custom`,
    persona
  );

/** @domain calls */
export const getLastAgentSimulationRun = (agentConfigId: string, personaName: string) =>
  http().get<AgentSimulationRun | null>(`/calls/agents/${agentConfigId}/runs/last`, {
    params: { persona_name: personaName },
  });

// Clinic Search and Knowledge Base Generation
/** @domain calls */
export const searchClinics = (clinicName: string, region?: string) => {
  return http().post<{
    clinics: Array<{
      name: string;
      website?: string;
      address?: string;
      description?: string;
    }>;
  }>('/calls/knowledge-base/search-clinics', {
    clinic_name: clinicName,
    region,
  });
};

/** @domain calls */
export const generateKnowledgeBase = (websiteUrl: string) => {
  return http().post<{
    knowledge_base: string;
    source_url: string;
  }>('/calls/knowledge-base/generate', {
    website_url: websiteUrl,
  });
};
